/* eslint-disable jsx-a11y/anchor-is-valid */
import { useQuery } from '@tanstack/react-query';
import { memo, useEffect, useState, useCallback } from 'react';

import { Box, Grid, Pagination } from '@core/component';
import { ARTICLES_TO_SHOW } from '@core/constant';
import { useContextMediaQuery, useContextRouting, useContextAlternate } from '@core/context';
import { useLanguage, useQueryApi } from '@core/hook';
import { getSanityQuery } from '@core/sanity';
import { ERouting, ViewCMSProps } from '@core/type';
import { HomepageArticlesResponse, EQueryKey } from '@core/type/api';
import { SanityBlogHome, SanityBlogHomeArticles } from '@core/type/sanity';
import { getLanguageFromTags } from '@core/util';
import { LeadMagnet, SpotlightBanner } from '@core/widget';

import { BlogArticle } from './BlogArticle';
import { BlogHomeMainArticle } from './components/BlogHomeMainArticle';

const LM_INDEX = 5;

export const ViewBlogList = memo(({ cms }: ViewCMSProps<SanityBlogHome>) => {
  const { mainArticle, articlesCount } = cms;
  const [isSubscriptionSuccessful, setIsSubscriptionSuccessful] = useState(false);
  const { query, setQuery } = useContextRouting();
  const { isPhone } = useContextMediaQuery();
  const { language } = useLanguage();
  const { setAlternates } = useContextAlternate();

  const { data, isLoading } = useQueryApi<HomepageArticlesResponse>(EQueryKey.COMPONENT_ARTICLES);
  const { leadMagnet } = data ?? {};
  const perPage = leadMagnet ? ARTICLES_TO_SHOW - 1 : ARTICLES_TO_SHOW;

  const { data: blogArticles } = useQuery({
    ...getSanityQuery<SanityBlogHomeArticles>('blogHomeArticlesQuery', {
      language: getLanguageFromTags(language),
      page: query.page as string,
      perPage: perPage,
      mainArticleId: cms.mainArticle?._id ?? '',
    }),
    keepPreviousData: true,
    enabled: !isLoading,
  });

  const changePage = useCallback(
    (page: number) => {
      setQuery(page === 1 ? {} : { page: page.toString() });
    },
    [language],
  );

  useEffect(() => {
    if (query.page && blogArticles?.length <= 0) {
      changePage(1);
    }
    setAlternates();
  }, [blogArticles]);

  return (
    <>
      {!(Number(query?.page) && Number(query?.page) > 1) && mainArticle?.pageSlug?.slug && (
        <BlogHomeMainArticle
          mainArticle={mainArticle}
          isSubscriptionSuccessful={isSubscriptionSuccessful}
        />
      )}

      {blogArticles?.length > 0 && (
        <Box paddingBottom={isPhone ? '104px' : isSubscriptionSuccessful ? '224px' : '144px'}>
          <Grid isContainer rowSpacing={64} columnSpacing={30}>
            {blogArticles?.map((article, index) =>
              leadMagnet && index === LM_INDEX - 1 ? (
                <Grid xs={12} lg={8} key={article.pageSlug?.slug.current}>
                  <Grid isContainer rowSpacing={64} columnSpacing={30}>
                    <Grid xs={12} md={6}>
                      <BlogArticle article={article} />
                    </Grid>

                    <Grid xs={12} md={6}>
                      <LeadMagnet leadMagnet={leadMagnet} requestPage={ERouting.BLOG_HOME} />
                    </Grid>
                  </Grid>
                </Grid>
              ) : (
                <Grid xs={12} md={6} lg={4} key={article.pageSlug?.slug.current}>
                  <BlogArticle article={article} />
                </Grid>
              ),
            )}
            {leadMagnet && blogArticles.length < LM_INDEX && (
              <Grid xs={12} md={6} lg={4}>
                <LeadMagnet leadMagnet={leadMagnet} requestPage={ERouting.BLOG_HOME} />
              </Grid>
            )}
          </Grid>

          <Box marginTop={isPhone ? '56px' : '80px'}>
            <Pagination
              page={Number(query?.page) || 1}
              perPage={perPage}
              itemsCount={articlesCount}
              onPageChange={changePage}
            />
          </Box>
        </Box>
      )}

      {!isSubscriptionSuccessful && <SpotlightBanner fn={setIsSubscriptionSuccessful} />}
    </>
  );
});

ViewBlogList.displayName = 'ViewBlogList';
